import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Phone, ArrowUpRight, ShieldCheck } from "lucide-react";
import { IMAGES } from "@/data/content";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { AdmissionBadge, ApplyButton } from "@/components/site/AdmissionBadge";
import { telHref } from "@/lib/links";

const HEAD_LINES = ["Build Your Pathway", "to University", "Admission"];

const lineV = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] } }),
};
const fade = {
  hidden: { opacity: 0, y: 18 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: 0.6 + i * 0.1, ease: [0.22, 1, 0.36, 1] } }),
};

export function Hero() {
  const { config } = useSiteConfig();
  return (
    <section
      className="relative overflow-hidden text-white"
      style={{ background: "radial-gradient(120% 90% at 80% 0%, #16347A 0%, #0A1E4E 45%, #071538 100%)" }}
      data-testid="hero-section"
    >
      <div className="absolute inset-0 paper-grain opacity-40" />

      <div className="container-x relative grid lg:grid-cols-2 gap-10 lg:gap-14 py-16 sm:py-20 lg:py-24 items-center">
        <div className="max-w-2xl">
          <motion.div variants={fade} custom={-2} initial="hidden" animate="show" className="flex flex-wrap items-center gap-3">
            <AdmissionBadge />
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-100/70">
              <ShieldCheck size={14} className="text-[#D4AF37]" /> Ignatius Ajuru University of Education
            </span>
          </motion.div>

          <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-[4.2rem] font-bold leading-[1.03]">
            {HEAD_LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span variants={lineV} custom={i} initial="hidden" animate="show" className="block">
                  {i === 2 ? <span className="text-[#D4AF37]">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p variants={fade} custom={0} initial="hidden" animate="show" className="mt-4 text-sm font-bold uppercase tracking-[0.25em] text-[#D4AF37]">
            JUPEB &amp; IJMB Programmes
          </motion.p>

          <motion.p variants={fade} custom={1} initial="hidden" animate="show" className="mt-5 max-w-xl text-base sm:text-lg leading-relaxed text-blue-100/85">
            The School of Basic Studies, Ignatius Ajuru University of Education, provides structured academic
            programmes designed to prepare qualified candidates for advanced-level examinations and eligible
            Direct Entry opportunities.
          </motion.p>

          <motion.div variants={fade} custom={2} initial="hidden" animate="show" className="mt-8 flex flex-wrap items-center gap-3">
            <ApplyButton className="btn-red" testId="hero-apply-now-button">
              Apply Now <ArrowUpRight size={16} />
            </ApplyButton>
            <Link to="/jupeb" className="btn-outline-gold" data-testid="hero-explore-button">
              Explore JUPEB &amp; IJMB
            </Link>
          </motion.div>

          <motion.a
            variants={fade} custom={3} initial="hidden" animate="show"
            href={telHref(config.phonePrimary)}
            className="mt-6 inline-flex items-center gap-2 text-lg font-bold text-white hover:text-[#D4AF37] transition-colors"
            data-testid="hero-phone-link"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#D4AF37] text-[#0A1E4E]"><Phone size={16} /></span>
            {config.phonePrimary}
          </motion.a>
        </div>

        <motion.div
          className="relative hidden lg:block"
          initial={{ opacity: 0, y: 40, rotate: 1.5 }}
          animate={{ opacity: 1, y: 0, rotate: 0 }}
          transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="absolute -inset-3 rounded-sm bg-[#D4AF37]/20 blur-2xl" />
          <div className="relative rounded-sm border border-[#D4AF37]/40 bg-white/[0.03] p-2 shadow-2xl backdrop-blur-sm">
            <img
              src={IMAGES.flyerPortrait}
              alt="IAUE School of Basic Studies JUPEB & IJMB admission flyer"
              className="w-full rounded-sm object-contain"
              loading="eager"
            />
          </div>
        </motion.div>
      </div>

      {/* gold rule */}
      <div className="h-1 w-full" style={{ background: "linear-gradient(90deg,#D4AF37,#F5B301,#D4AF37)" }} />
    </section>
  );
}
